import { Dialog } from 'primereact/dialog';
import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { CompilerActionSecondaryButton, PrimaryButton, SecondaryButton } from 'components/v2/buttons';
import { AccountModel, ActionState, CompanyListModel, EPDModel, EPDStatus, VersionStateType } from 'services/EpdClient';
import { HelpBox } from 'components/v2/help-boxes';
import CompilerSelectControlledComponent from 'components/v2/epd-compiler-inputs/CompilerSelectControlledComponent';
import AdminService from 'services/admin/AdminService';
import AccountsService from 'services/admin/AccountsService';
import AdminEpdService from 'services/admin/AdminEpdService';
import { toaster } from 'components/v2/toast';
import { useQueryClient } from '@tanstack/react-query';
import { QUERY_KEYS } from 'services/api/constants';
import OverlaySpinner from 'components/spinners/OverlaySpinner';
import { ProcessStatus } from 'constants/constants';
import { Option } from 'types/types';
import AuthService from 'services/AuthService';

interface Props {
  epd: EPDModel | undefined;
}

const TransferEPDDialog: React.FunctionComponent<Props> = ({ epd }) => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [companies, setCompanies] = useState<Option[]>([]);
  const [selectedCompany, setSelectedCompany] = useState<string | undefined | null>();
  const [accounts, setAccounts] = useState<Option[]>();
  const [selectedAccount, setSelectedAccount] = useState<string | undefined | null>();
  const [status, setStatus] = useState<ProcessStatus>(ProcessStatus.None);
  const isAdmin = AuthService.isAdmin();
  const queryClient = useQueryClient();

  useEffect(() => {
    const fetchAccounts = async () => {
      const result = await AccountsService.getAccounts(selectedCompany as string);
      const accountOptions: Option[] = result?.map((account: AccountModel) => {
        return { value: account.id, label: account.name } as Option;
      });
      setAccounts(accountOptions);
      if (accountOptions?.length === 1) {
        setSelectedAccount(accountOptions[0]?.value);
      }
    };
    fetchAccounts();
  }, [selectedCompany]);

  useEffect(() => {
    const fetchCompanies = async () => {
      const result = await AdminService.getCompanies(undefined, undefined, undefined);
      const companyOptions: any = result?.map((company: CompanyListModel) => {
        return { value: company.id, label: company.name };
      });
      setCompanies(companyOptions);
    };
    fetchCompanies();
  }, []);

  const handleCompanySelect = (name: string, val: any) => {
    setSelectedAccount(null);
    if (!val) {
      setSelectedCompany(null);
    }
    setSelectedCompany(val);
  };

  const handleAccountSelect = (name: string, val: any) => {
    if (!val) {
      setSelectedAccount(null);
    }
    setSelectedAccount(val);
  };

  const onMoveEpd = async () => {
    try {
      setStatus(ProcessStatus.Fetching);
      setIsDialogOpen(false);
      await AdminEpdService.moveEpd(epd?.id as string, selectedAccount as string);
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.EPD_MODEL, epd?.id],
      });
      setStatus(ProcessStatus.Success);
      toaster({
        severity: 'success',
        summary: 'Success',
        details: 'EPD has been moved',
      });
      setSelectedCompany(null);
      setSelectedAccount(null);
    } catch (error) {
      setStatus(ProcessStatus.Error);
      toaster({
        severity: 'error',
        summary: 'Error',
        details: 'EPD hasn`t been moved',
      });
    }
  };

  const footerContent = (
    <div>
      <SecondaryButton label="Cancel" onClick={() => setIsDialogOpen(false)} outlined />
      <PrimaryButton label="Submit" onClick={onMoveEpd} autoFocus disabled={!selectedAccount || !selectedCompany} />
    </div>
  );

  return (
    <>
      {status === ProcessStatus.Fetching && <OverlaySpinner />}
      {(isAdmin || (epd?.status === EPDStatus.Reserved && epd?.versionState === VersionStateType.Draft)) && (
        <CompilerActionSecondaryButton
          actionState={ActionState.Enabled}
          label="Transfer EPD"
          onClick={() => setIsDialogOpen(true)}
          outlined
        />
      )}
      <Dialog
        header="Transfer EPD to another organisation"
        visible={isDialogOpen}
        style={{ width: 560, overflow: 'visible' }}
        footer={footerContent}
        onHide={() => setIsDialogOpen(false)}
        closable={false}
        contentStyle={{ overflow: 'visible' }}
      >
        <DialogContentContainer>
          <HelpBox style={{ marginTop: 0, overflowWrap: 'anywhere', gap: 0 }}>
            <span>Please select an organisation and account to transfer</span>
            <span> {epd?.name}.</span>
          </HelpBox>
          <CompilerSelectControlledComponent
            label="Organisation"
            name="organisation"
            options={companies}
            value={selectedCompany ? companies?.find((x) => x.value === selectedCompany) : null}
            onChanged={handleCompanySelect}
            placeholder="Select..."
          />
          <CompilerSelectControlledComponent
            label="Account"
            name="account"
            options={accounts}
            value={selectedAccount ? accounts?.find((x) => x.value === selectedAccount) : null}
            onChanged={handleAccountSelect}
            placeholder="Select..."
          />
        </DialogContentContainer>
      </Dialog>
    </>
  );
};

const DialogContentContainer = styled.div`
  padding: 0.25rem 2rem 2rem 2rem;
  display: grid;
  gap: 30px;
`;

export default TransferEPDDialog;
